import { useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { Upsell_Events_Stream_Url } from '../api-endpoints/endpoints';
import { isDemoMode } from '../services/_demo/demoMode';

/**
 * Instant live updates via Server-Sent Events. While the screen is focused, we
 * hold an EventSource to the Upsell API's `/v1/events/stream`; when the backend
 * ingests a catalog change (item/category/tag deleted or published) it pushes a
 * `catalog.changed` event and we call `onChange()` immediately — so screens
 * update in a fraction of a second instead of waiting for the poll interval
 * (the poll stays as a backstop if the stream drops).
 *
 * Web-only (EventSource is a browser API; guarded for native/SSR). Cookies ride
 * along via withCredentials, so the platform session authenticates the stream —
 * no token plumbing needed. The browser auto-reconnects on transient drops.
 */
export function useLiveCatalogRefresh(onChange: () => void): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useFocusEffect(
    useCallback(() => {
      // No live stream in offline/demo mode (there's no backend to stream from).
      const ESCtor = (globalThis as unknown as { EventSource?: new (u: string, o?: { withCredentials?: boolean }) => {
        addEventListener: (t: string, cb: () => void) => void;
        close: () => void;
        onerror: ((e: unknown) => void) | null;
      } }).EventSource;
      if (isDemoMode() || !ESCtor) return;

      const es = new ESCtor(Upsell_Events_Stream_Url, { withCredentials: true });
      es.addEventListener('catalog.changed', () => onChangeRef.current());
      // On error the browser reconnects automatically (honouring the server's
      // `retry:`); nothing to do but avoid an unhandled error.
      es.onerror = () => {};
      return () => es.close();
    }, []),
  );
}
